import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, Legend, ResponsiveContainer } from 'recharts'
import { Share2, Copy, Check, ArrowLeft, BarChart3, AlertCircle } from 'lucide-react'
import { useLang } from '../i18n/LangContext'

interface SharedAgentSummary {
  name: string
  mode: string
  model?: string
  avg_total: number
  breakdown: { accuracy: number; process: number; efficiency: number; constraint: number }
  scores: number[] // per-task totals (for stddev)
  stats?: { mean: number; std: number; ci_lower?: number; ci_upper?: number }
  judge_avg?: number | null
}

interface SharedResult {
  generated_at: string
  dataset_version?: string
  tasks: Array<{ id: string; title: string }>
  agents: SharedAgentSummary[]
  max_steps: number
  // Pairwise t-tests
  ttests?: Array<{ a: string; b: string; t: number; significant: boolean }>
}

const DIMENSIONS = [
  { key: 'accuracy', zh: '准确性', en: 'Accuracy' },
  { key: 'process', zh: '过程', en: 'Process' },
  { key: 'efficiency', zh: '效率', en: 'Efficiency' },
  { key: 'constraint', zh: '约束', en: 'Constraint' },
] as const

function scoreColor(total: number): string {
  if (total >= 80) return 'var(--color-success)'
  if (total >= 60) return 'var(--color-warning)'
  return 'var(--color-danger)'
}

export default function ShareViewPage() {
  const { lang } = useLang()
  const tr = (zh: string, en: string) => (lang === 'zh' ? zh : en)
  const navigate = useNavigate()
  const [data, setData] = useState<SharedResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    try {
      const hash = window.location.hash.replace(/^#/, '')
      if (!hash) {
        setError(tr('未找到分享数据', 'No shared data found in URL'))
        return
      }
      // Decode base64 → JSON
      const json = atob(hash)
      const parsed = JSON.parse(json) as SharedResult
      setData(parsed)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid shared data')
    }
  }, [tr])

  const radarData = useMemo(() => {
    if (!data) return []
    return DIMENSIONS.map(dim => {
      const row: Record<string, number | string> = { dimension: tr(dim.zh, dim.en) }
      data.agents.forEach(a => {
        row[a.name] = Math.round(a.breakdown[dim.key])
      })
      return row
    })
  }, [data, tr])

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (error) {
    return (
      <div className="container-page px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-xl mx-auto text-center">
          <AlertCircle size={48} className="mx-auto mb-4" style={{ color: 'var(--color-danger)' }} />
          <h1 className="text-xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
            {tr('分享链接无效', 'Invalid Share Link')}
          </h1>
          <p className="text-sm mb-6" style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
            style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
          >
            <ArrowLeft size={15} />
            {tr('返回首页', 'Back to Home')}
          </button>
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="container-page px-4 py-12 text-center" style={{ color: 'var(--color-text-secondary)' }}>
        {tr('加载中...', 'Loading...')}
      </div>
    )
  }

  return (
    <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <Share2 size={24} style={{ color: 'var(--color-accent)' }} />
            {tr('评测结果分享', 'Shared Evaluation Results')}
          </h1>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            {tr('生成时间', 'Generated')}: {new Date(data.generated_at).toLocaleString()}
            {' · '}
            {tr('任务数', 'Tasks')}: {data.tasks.length}
            {' · '}
            {tr('Agent数', 'Agents')}: {data.agents.length}
            {data.dataset_version && (
              <>
                {' · '}
                {tr('数据集版本', 'Dataset')}: <span style={{ color: 'var(--color-accent)', fontWeight: 600 }}>{data.dataset_version}</span>
              </>
            )}
          </p>
        </div>
        <button
          onClick={handleCopyLink}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
        >
          {copied ? <Check size={15} /> : <Copy size={15} />}
          {copied ? (tr('已复制', 'Copied!')) : (tr('复制链接', 'Copy Link'))}
        </button>
      </div>

      {/* Read-only notice */}
      <div
        className="mb-6 px-4 py-2.5 rounded-lg text-sm flex items-center gap-2"
        style={{
          backgroundColor: 'rgba(108,140,255,0.08)',
          border: '1px solid rgba(108,140,255,0.2)',
          color: 'var(--color-accent)',
        }}
      >
        <BarChart3 size={15} />
        {tr('此为只读分享视图。如需复现评测，请使用 YAML 配置导入功能。', 'This is a read-only shared view. To reproduce, use the YAML config import feature.')}
      </div>

      {/* Task list */}
      <div className="mb-6 rounded-xl p-5" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
        <h2 className="text-base font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>
          {tr('评测任务', 'Evaluated Tasks')} ({data.tasks.length})
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {data.tasks.map(t => (
            <div key={t.id} className="text-sm px-3 py-2 rounded" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}>
              {t.title}
            </div>
          ))}
        </div>
      </div>

      {/* Average scores table */}
      <div className="mb-6 rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
        <div className="px-5 py-3 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <h2 className="text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {tr('Agent 平均得分', 'Agent Average Scores')}
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                <th className="text-left px-4 py-2.5 font-medium" style={{ color: 'var(--color-text-secondary)' }}>{tr('Agent', 'Agent')}</th>
                <th className="text-left px-4 py-2.5 font-medium" style={{ color: 'var(--color-text-secondary)' }}>{tr('模式', 'Mode')}</th>
                <th className="text-right px-4 py-2.5 font-medium" style={{ color: 'var(--color-text-secondary)' }}>{tr('平均分', 'Avg Score')}</th>
                <th className="text-right px-4 py-2.5 font-medium" style={{ color: 'var(--color-text-secondary)' }}>{tr('标准差', 'Std Dev')}</th>
                <th className="text-right px-4 py-2.5 font-medium" style={{ color: 'var(--color-text-secondary)' }}>95% CI</th>
                <th className="text-right px-4 py-2.5 font-medium" style={{ color: 'var(--color-text-secondary)' }}>{tr('LLM评判', 'LLM Judge')}</th>
              </tr>
            </thead>
            <tbody>
              {data.agents.map(a => (
                <tr key={a.name} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                  <td className="px-4 py-2.5 font-medium" style={{ color: 'var(--color-text-primary)' }}>{a.name}</td>
                  <td className="px-4 py-2.5" style={{ color: 'var(--color-text-muted)' }}>
                    {a.mode}{a.model ? ` / ${a.model}` : ''}
                  </td>
                  <td className="px-4 py-2.5 text-right font-bold" style={{ color: scoreColor(a.avg_total) }}>
                    {a.avg_total.toFixed(1)}
                  </td>
                  <td className="px-4 py-2.5 text-right" style={{ color: 'var(--color-text-secondary)' }}>
                    {a.stats ? `±${a.stats.std.toFixed(2)}` : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    {a.stats?.ci_lower !== undefined && a.stats?.ci_upper !== undefined
                      ? `[${a.stats.ci_lower.toFixed(1)}, ${a.stats.ci_upper.toFixed(1)}]`
                      : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right" style={{ color: a.judge_avg != null ? 'var(--color-accent)' : 'var(--color-text-muted)' }}>
                    {a.judge_avg != null ? a.judge_avg : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Radar chart */}
      {data.agents.length > 0 && (
        <div className="mb-6 rounded-xl p-5" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
          <h2 className="text-base font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
            {tr('多维评分对比', 'Multi-dimensional Comparison')}
          </h2>
          <div style={{ width: '100%', height: 360 }}>
            <ResponsiveContainer>
              <RadarChart data={radarData}>
                <PolarGrid stroke="var(--color-border)" />
                <PolarAngleAxis dataKey="dimension" tick={{ fill: 'var(--color-text-secondary)', fontSize: 12 }} />
                {data.agents.map((a, i) => (
                  <Radar
                    key={a.name}
                    name={a.name}
                    dataKey={a.name}
                    stroke={AGENT_COLORS[i % AGENT_COLORS.length]}
                    fill={AGENT_COLORS[i % AGENT_COLORS.length]}
                    fillOpacity={0.15}
                  />
                ))}
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Pairwise t-tests */}
      {data.ttests && data.ttests.length > 0 && (
        <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
          <div className="px-5 py-3 border-b" style={{ borderColor: 'var(--color-border)' }}>
            <h2 className="text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              {tr('统计显著性（两两t检验）', 'Statistical Significance (Pairwise t-tests)')}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                  <th className="text-left px-4 py-2.5 font-medium" style={{ color: 'var(--color-text-secondary)' }}>{tr('对比A', 'Agent A')}</th>
                  <th className="text-left px-4 py-2.5 font-medium" style={{ color: 'var(--color-text-secondary)' }}>{tr('对比B', 'Agent B')}</th>
                  <th className="text-right px-4 py-2.5 font-medium" style={{ color: 'var(--color-text-secondary)' }}>t</th>
                  <th className="text-center px-4 py-2.5 font-medium" style={{ color: 'var(--color-text-secondary)' }}>{tr('显著(p<0.05)', 'Significant')}</th>
                </tr>
              </thead>
              <tbody>
                {data.ttests.map((tt, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                    <td className="px-4 py-2.5" style={{ color: 'var(--color-text-primary)' }}>{tt.a}</td>
                    <td className="px-4 py-2.5" style={{ color: 'var(--color-text-primary)' }}>{tt.b}</td>
                    <td className="px-4 py-2.5 text-right" style={{ color: 'var(--color-text-secondary)' }}>{tt.t.toFixed(3)}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span
                        className="inline-block px-2 py-0.5 rounded text-xs font-medium"
                        style={{
                          backgroundColor: tt.significant ? 'rgba(74,222,128,0.15)' : 'rgba(148,163,184,0.15)',
                          color: tt.significant ? 'var(--color-success)' : 'var(--color-text-muted)',
                        }}
                      >
                        {tt.significant ? tr('是', 'Yes') : tr('否', 'No')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

const AGENT_COLORS = ['#6c8cff', '#4ade80', '#fbbf24', '#f87171']
